/**
 * GanttChartV2 - マイルストーン対応ガントチャート
 *
 * ⚠️ このコンポーネントは開発中です
 * 既存の GanttChartView.tsx とは完全に分離しています
 *
 * 機能:
 * - マイルストーン行の表示
 * - チーム統合ビュー（複数プロジェクト表示）
 * - 担当者フィルタ・ソート
 * - ズーム（拡大縮小）- 細かい段階
 * - ワイド/全画面切り替え
 * - スムーズドラッグスクロール
 * - タスク追加機能（モーダル）
 * - ガント独自のタスク順序管理（ドラッグ並び替え）
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ChevronLeft,
  ChevronRight,
  Users,
  Calendar,
  FolderOpen,
  ZoomIn,
  ZoomOut,
  PanelLeftClose,
  PanelLeft,
  Plus,
  Square,
  X,
  GripVertical,
  Flag,
  Trash2,
  Maximize2,
  Minimize2,
  Settings,
  Pencil,
} from 'lucide-react'
import { useStore } from '../store/useStore'
import { supabase, type Todo } from '../lib/supabase'
import { getUserColorFromId, getUserInitial } from '../utils/userColor'
import AddTaskModal from './AddTaskModal'
import TaskDetailModal from './TaskDetailModal'
import MilestoneTemplateModal from './MilestoneTemplateModal'
import {
  getMilestonesByProjectIds,
  getMilestoneTemplatesByTeamId,
  getMilestoneTemplatesByUserId,
  initializePersonalTemplatesIfNeeded,
  createProjectMilestone,
  updateProjectMilestone,
  deleteProjectMilestone,
  createMilestoneTemplate,
  createPersonalMilestoneTemplate,
  updateMilestoneTemplate,
  deleteMilestoneTemplate,
  type ProjectMilestone,
  type MilestoneTemplate as DBMilestoneTemplate,
} from '../services/milestoneService'
import { useAuth } from '../contexts/AuthContext'

// マイルストーン型（フロントエンド用 camelCase）
type Milestone = {
  id: string
  projectId: string
  templateId: string | null
  title: string
  startDate: string
  endDate: string | null
  iconType: 'emoji' | 'lucide'
  iconValue: string
  color: string
  sortOrder: number
}

// マイルストーンテンプレート型（フロントエンド用 camelCase）
type MilestoneTemplate = {
  id: string
  teamId: string
  title: string
  iconType: 'emoji' | 'lucide'
  iconValue: string
  color: string
  sortOrder: number
}

// DB型からフロントエンド型への変換
const convertDBMilestoneToLocal = (dbMilestone: ProjectMilestone): Milestone => ({
  id: dbMilestone.id,
  projectId: dbMilestone.project_id,
  templateId: dbMilestone.template_id,
  title: dbMilestone.title,
  startDate: dbMilestone.start_date,
  endDate: dbMilestone.end_date,
  iconType: dbMilestone.icon_type,
  iconValue: dbMilestone.icon_value,
  color: dbMilestone.color,
  sortOrder: dbMilestone.sort_order,
})

const convertDBTemplateToLocal = (dbTemplate: DBMilestoneTemplate): MilestoneTemplate => ({
  id: dbTemplate.id,
  teamId: dbTemplate.team_id || '',  // 個人テンプレートの場合は空文字
  title: dbTemplate.title,
  iconType: dbTemplate.icon_type,
  iconValue: dbTemplate.icon_value,
  color: dbTemplate.color,
  sortOrder: dbTemplate.sort_order,
})


// ズームレベル設定（細かい段階に変更）- 最小幅を制限
const ZOOM_LEVELS = [
  { label: '日+', dayWidth: 60, showDayOfWeek: true, dateFormat: 'full' },
  { label: '日', dayWidth: 40, showDayOfWeek: true, dateFormat: 'full' },
  { label: '日-', dayWidth: 28, showDayOfWeek: true, dateFormat: 'day' },
  { label: '週+', dayWidth: 18, showDayOfWeek: false, dateFormat: 'day' },
  { label: '週', dayWidth: 14, showDayOfWeek: false, dateFormat: 'day' },
]
const DEFAULT_ZOOM_LEVEL = 1 // 「日」がデフォルト

// 表示設定
const ROW_HEIGHT = 36
const MILESTONE_ROW_HEIGHT = 40
const PROJECT_HEADER_HEIGHT = 32
const SIDEBAR_WIDTH_WIDE = 240
const SIDEBAR_WIDTH_NARROW = 48

// ステータスフィルタ型
type FilterStatus = 'pending' | 'in_progress' | 'completed' | 'deleted'

interface GanttChartV2Props {
  className?: string
  filterStatuses?: FilterStatus[]  // 外部からのフィルタ指定（GanttChartV2Pageから渡される）
}


// 確認ダイアログ
function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
}: {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
}) {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
        <div className="p-4 border-b">
          <h3 className="font-bold text-lg">{title}</h3>
        </div>
        <div className="p-4">
          <p className="text-gray-600">{message}</p>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
          >
            キャンセル
          </button>
          <button
            onClick={() => {
              onConfirm()
              onClose()
            }}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            実行
          </button>
        </div>
      </div>
    </div>
  )
}

// 日程変更確認ダイアログ（TODO: DB連携時に使用予定）
// function DateChangeConfirmDialog({
//   isOpen,
//   onClose,
//   onConfirm,
//   taskTitle,
//   mode,
//   oldStartDate,
//   oldEndDate,
//   newStartDate,
//   newEndDate,
// }: {
//   isOpen: boolean
//   onClose: () => void
//   onConfirm: () => void
//   taskTitle: string
//   mode: 'move' | 'resize-left' | 'resize-right'
//   oldStartDate: string
//   oldEndDate: string
//   newStartDate: string
//   newEndDate: string
// }) {
//   if (!isOpen) return null
//
//   const formatDate = (dateStr: string) => {
//     const d = new Date(dateStr)
//     return `${d.getMonth() + 1}/${d.getDate()}`
//   }
//
//   const modeLabel = mode === 'move' ? '移動' : mode === 'resize-left' ? '開始日変更' : '終了日変更'
//
//   return (
//     <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//       <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
//         <div className="p-4 border-b bg-indigo-50">
//           <h3 className="font-bold text-lg text-indigo-800">日程変更の確認</h3>
//         </div>
//         <div className="p-4 space-y-3">
//           <p className="text-sm text-gray-600">
//             <span className="font-medium">「{taskTitle}」</span>の{modeLabel}を確定しますか？
//           </p>
//           <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
//             <div className="flex justify-between">
//               <span className="text-gray-500">変更前:</span>
//               <span className="font-mono">{formatDate(oldStartDate)} 〜 {formatDate(oldEndDate)}</span>
//             </div>
//             <div className="flex justify-between text-indigo-600">
//               <span className="font-medium">変更後:</span>
//               <span className="font-mono font-medium">{formatDate(newStartDate)} 〜 {formatDate(newEndDate)}</span>
//             </div>
//           </div>
//         </div>
//         <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
//           <button
//             onClick={onClose}
//             className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded"
//           >
//             キャンセル
//           </button>
//           <button
//             onClick={() => {
//               onConfirm()
//               onClose()
//             }}
//             className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
//           >
//             確定
//           </button>
//         </div>
//       </div>
//     </div>
//   )
// }


// マイルストーン選択モーダル（日付クリック or 既存マイルストーンクリック時）
function MilestoneSelectorModal({
  isOpen,
  onClose,
  projectId: _projectId,
  projectName,
  projectColor,
  selectedDate,
  templates,
  existingMilestone,
  onSelect,
  onDelete,
  onDateChange,
  onOpenTemplateEditor,
}: {
  isOpen: boolean
  onClose: () => void
  projectId: string
  projectName: string
  projectColor: string
  selectedDate: string
  templates: MilestoneTemplate[]
  existingMilestone: Milestone | null  // 既存マイルストーンをクリックした場合
  onSelect: (template: MilestoneTemplate) => void
  onDelete: (milestoneId: string) => void
  onDateChange?: (milestoneId: string, newDate: string) => void
  onOpenTemplateEditor?: () => void
}) {
  const [editDate, setEditDate] = useState(selectedDate)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  // モーダルが開く度に日付をリセット
  useEffect(() => {
    setEditDate(selectedDate)
    setShowDeleteConfirm(false)
  }, [selectedDate, isOpen])

  if (!isOpen) return null

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`
  }

  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  const confirmDelete = () => {
    if (existingMilestone) {
      onDelete(existingMilestone.id)
      onClose()
    }
  }

  const handleDateChange = () => {
    if (existingMilestone && onDateChange && editDate !== selectedDate) {
      onDateChange(existingMilestone.id, editDate)
      onClose()
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-4 border-b" style={{ backgroundColor: projectColor + '20' }}>
          <div className="flex items-center gap-2">
            <Flag className="w-5 h-5" style={{ color: projectColor }} />
            <h3 className="font-bold text-lg">
              {existingMilestone ? 'マイルストーンを変更' : 'マイルストーンを配置'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 情報 */}
        <div className="px-4 py-3 bg-gray-50 border-b">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{projectName}</span>
          </div>
          {existingMilestone ? (
            <>
              <div className="flex items-center gap-2 mt-2 p-2 bg-white rounded border">
                <span className="text-xl">{existingMilestone.iconValue}</span>
                <span className="font-medium" style={{ color: existingMilestone.color }}>
                  {existingMilestone.title}
                </span>
              </div>
              {/* 日付編集 */}
              <div className="mt-3">
                <label className="block text-xs text-gray-500 mb-1">日付</label>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    className="flex-1 border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  {editDate !== selectedDate && (
                    <button
                      onClick={handleDateChange}
                      className="px-3 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
                    >
                      変更
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-lg font-bold text-indigo-600 mt-1">
              {formatDate(selectedDate)}
            </div>
          )}
        </div>

        {/* テンプレート一覧 */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="text-xs text-gray-500 mb-2">
            {existingMilestone ? 'テンプレートを変更:' : 'テンプレートを選択:'}
          </div>
          {templates.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              <p>テンプレートがありません</p>
              {onOpenTemplateEditor ? (
                <button
                  onClick={onOpenTemplateEditor}
                  className="mt-3 px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 flex items-center gap-2 mx-auto"
                >
                  <Settings className="w-4 h-4" />
                  テンプレートを作成
                </button>
              ) : (
                <p className="text-sm mt-2">ツールバーの「マイルストーン設定」からテンプレートを追加してください</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {templates.map(template => {
                const isCurrentTemplate = existingMilestone?.templateId === template.id
                return (
                  <button
                    key={template.id}
                    onClick={() => onSelect(template)}
                    className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all group ${
                      isCurrentTemplate
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'hover:border-indigo-400 hover:bg-indigo-50'
                    }`}
                  >
                    <span className="text-3xl group-hover:scale-110 transition-transform">
                      {template.iconValue}
                    </span>
                    <span
                      className="text-sm font-medium text-center truncate w-full"
                      style={{ color: template.color }}
                    >
                      {template.title}
                    </span>
                    {isCurrentTemplate && (
                      <span className="text-xs text-indigo-500">現在選択中</span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
          {/* テンプレート編集ボタン（テンプレートがある場合も表示） */}
          {templates.length > 0 && onOpenTemplateEditor && (
            <button
              onClick={onOpenTemplateEditor}
              className="mt-4 w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <Settings className="w-4 h-4" />
              テンプレートを編集・追加
            </button>
          )}
        </div>

        {/* フッター */}
        <div className={`px-4 py-3 border-t bg-gray-50 ${existingMilestone ? 'flex justify-between' : ''}`}>
          {existingMilestone && (
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              削除
            </button>
          )}
          <button
            onClick={onClose}
            className={`px-4 py-2 text-gray-600 hover:bg-gray-200 rounded transition-colors ${
              existingMilestone ? '' : 'w-full'
            }`}
          >
            キャンセル
          </button>
        </div>
      </div>

      {/* 削除確認ダイアログ */}
      {showDeleteConfirm && existingMilestone && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="p-4 border-b">
              <h3 className="font-bold text-lg">マイルストーンを削除</h3>
            </div>
            <div className="p-4">
              <p className="text-gray-600">
                「{existingMilestone.title}」を削除しますか？
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                キャンセル
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                削除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// マイルストーン日付変更確認ダイアログ
function MilestoneDateChangeDialog({
  isOpen,
  onClose,
  onConfirm,
  milestoneTitle,
  oldDate,
  newDate,
}: {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  milestoneTitle: string
  oldDate: string
  newDate: string
}) {
  if (!isOpen) return null

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return `${d.getMonth() + 1}/${d.getDate()}`
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
        <div className="p-4 border-b bg-amber-50">
          <h3 className="font-bold text-lg text-amber-800">マイルストーン移動の確認</h3>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm text-gray-600">
            <span className="font-medium">「{milestoneTitle}」</span>を移動しますか？
          </p>
          <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-500">変更前:</span>
              <span className="font-mono">{formatDate(oldDate)}</span>
            </div>
            <div className="flex justify-between text-amber-600">
              <span className="font-medium">変更後:</span>
              <span className="font-mono font-medium">{formatDate(newDate)}</span>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded"
          >
            キャンセル
          </button>
          <button
            onClick={() => {
              onConfirm()
              onClose()
            }}
            className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700"
          >
            確定
          </button>
        </div>
      </div>
    </div>
  )
}

// ガント表示用のタスク型（DBからの変換後）
interface GanttTask {
  id: string
  projectId: string
  title: string
  startDate: string | null
  endDate: string | null
  assigneeId: string | null
  assigneeName: string | null
  assigneeInitial: string
  avatarGradient: string
  progress: number
  priority: number
  status: 'pending' | 'in_progress' | 'completed' | 'deleted'
  ganttSortOrder: number
}

// ソート可能なタスク行コンポーネント（@dnd-kit用）
interface SortableTaskRowProps {
  task: GanttTask
  sidebarCollapsed: boolean
  sortBy: 'custom' | 'assignee'
  onClick?: (task: GanttTask) => void
  onEditClick?: (task: GanttTask) => void
}

function SortableTaskRow({ task, sidebarCollapsed, sortBy, onClick, onEditClick }: SortableTaskRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 'auto' as const,
    height: ROW_HEIGHT,
  }

  // カスタムソートのときは行全体をドラッグ可能にする
  const rowListeners = sortBy === 'custom' ? listeners : undefined

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-task-id={task.id}
      className={`flex items-center gap-1 px-2 text-sm border-b bg-white cursor-pointer hover:bg-gray-50 ${
        isDragging ? 'shadow-lg ring-2 ring-indigo-500' : ''
      } ${sortBy === 'custom' ? 'cursor-grab active:cursor-grabbing' : ''}`}
      title={`${task.title}\n（クリックでバーにスクロール）`}
      onClick={() => onClick?.(task)}
      {...attributes}
      {...rowListeners}
    >
      {/* ドラッグハンドルアイコン（カスタム順のときのみ表示） */}
      {sortBy === 'custom' && !sidebarCollapsed && (
        <div className="p-0.5 -ml-0.5">
          <GripVertical className="w-3 h-3 text-gray-400 flex-shrink-0" />
        </div>
      )}
      {/* 担当者バッジ */}
      {task.assigneeId && task.assigneeName && (
        <span
          className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-semibold flex-shrink-0 bg-gradient-to-br ${task.avatarGradient}`}
          title={task.assigneeName}
        >
          {task.assigneeInitial}
        </span>
      )}
      {!sidebarCollapsed && (
        <>
          <span className="truncate flex-1">{task.title}</span>
          {/* 編集ボタン */}
          <button
            onClick={(e) => {
              e.stopPropagation()
              onEditClick?.(task)
            }}
            className="p-1 rounded hover:bg-indigo-100 text-gray-400 hover:text-indigo-600 transition-colors flex-shrink-0"
            title="タスクを編集"
          >
            <Pencil className="w-3 h-3" />
          </button>
        </>
      )}
    </div>
  )
}

export function GanttChartV2({ className = '', filterStatuses = ['pending', 'in_progress'] }: GanttChartV2Props) {
  // ストアからデータを取得
  const { projects, todos, fetchProjects, fetchTodos, updateTodo, updateGanttTodosOrder, ganttFullscreenMode, setGanttFullscreenMode } = useStore()
  const { user } = useAuth()

  // 初回マウント時にデータをロード
  useEffect(() => {
    if (projects.length === 0) {
      fetchProjects()
    }
    if (todos.length === 0) {
      fetchTodos()
    }
  }, [projects.length, todos.length, fetchProjects, fetchTodos])

  // 表示範囲（デフォルト: 今日から前後1年）
  const today = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }, [])

  const [startDate] = useState(() => {
    const start = new Date(today)
    start.setFullYear(start.getFullYear() - 1)
    return start
  })

  // 前後1年 = 約730日
  const [displayDays] = useState(730)

  // ズームレベル（デフォルト: 日表示）
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM_LEVEL)
  const currentZoom = ZOOM_LEVELS[zoomLevel]

  // サイドバー折りたたみ状態
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  // フィルタ（チーム選択はストアで管理 - 最大化切り替え時も維持）
  const [selectedAssigneeId, _setSelectedAssigneeId] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'custom' | 'assignee'>('custom')

  // ストアからselectedProjectId、チーム選択、表示フラグを取得
  const {
    selectedProjectId,
    isPersonalSectionVisible,
    isTeamSectionVisible,
    ganttSelectedTeamId: selectedTeamId,
    setGanttSelectedTeamId: setSelectedTeamId,
  } = useStore()

  // 利用可能なチームのリスト（重複除去）- 全プロジェクトから
  const availableTeams = useMemo(() => {
    const teamsMap = new Map<string, { id: string; name: string }>()
    projects.forEach(p => {
      if (p.team_id && p.team) {
        teamsMap.set(p.team_id, { id: p.team_id, name: p.team.name })
      }
    })
    return Array.from(teamsMap.values())
  }, [projects])

  // 表示対象プロジェクトをフィルタ
  // 優先度: selectedProjectId > selectedTeamId > 全表示（目玉マーク反映）
  const filteredProjects = useMemo(() => {
    // 1. 特定のプロジェクトが選択されている場合（目玉マーク関係なく表示）
    if (selectedProjectId) {
      const project = projects.find(p => p.id === selectedProjectId)
      return project ? [project] : []
    }

    // 2. 全て表示の場合は目玉マークの状態を反映
    let filtered = projects.filter(p => p.name !== '一時保存')

    // 目玉マーク（show_in_all_projects）が false のプロジェクトを除外
    filtered = filtered.filter(p => p.show_in_all_projects !== false)

    // 個人プロジェクト・チームプロジェクトの表示/非表示を反映
    filtered = filtered.filter(p => {
      if (p.is_team_project) {
        return isTeamSectionVisible
      } else {
        return isPersonalSectionVisible
      }
    })

    // 3. チームが選択されている場合（チームでフィルタ）
    if (selectedTeamId) {
      filtered = filtered.filter(p => p.team_id === selectedTeamId)
    }

    return filtered
  }, [projects, selectedProjectId, selectedTeamId, isPersonalSectionVisible, isTeamSectionVisible])

  // TodoからGanttTaskへの変換
  const convertTodoToGanttTask = useCallback((todo: Todo): GanttTask => {
    const assignedUser = todo.assigned_user
    const assigneeName = assignedUser?.nickname || assignedUser?.email?.split('@')[0] || null
    return {
      id: todo.id,
      projectId: todo.project_id,
      title: todo.title,
      startDate: todo.start_date ? todo.start_date.split('T')[0] : null,
      endDate: todo.due_date ? todo.due_date.split('T')[0] : null,
      assigneeId: todo.assigned_to_user_id,
      assigneeName,
      assigneeInitial: todo.assigned_to_user_id ? getUserInitial(assignedUser?.nickname, assignedUser?.email) : '',
      avatarGradient: todo.assigned_to_user_id ? getUserColorFromId(todo.assigned_to_user_id) : '',
      progress: todo.progress_percentage,
      priority: todo.priority,
      status: todo.status,
      ganttSortOrder: todo.gantt_sort_order ?? todo.display_order,
    }
  }, [])

  // タスク管理（DBから取得したTodoを変換 + ステータスフィルタ適用）
  const ganttTasks = useMemo(() => {
    const projectIds = new Set(filteredProjects.map(p => p.id))
    const filtered = todos
      .filter(t => projectIds.has(t.project_id) && filterStatuses.includes(t.status as FilterStatus))
      .map(convertTodoToGanttTask)
    return filtered
  }, [todos, filteredProjects, convertTodoToGanttTask, filterStatuses])

  // マイルストーン管理（DBから取得）
  const [localMilestones, setLocalMilestones] = useState<Milestone[]>([])
  const [_milestonesLoading, setMilestonesLoading] = useState(false)

  // マイルストーンテンプレート管理（DBから取得）
  const [localMilestoneTemplates, setLocalMilestoneTemplates] = useState<MilestoneTemplate[]>([])
  const [_templatesLoading, setTemplatesLoading] = useState(false)

  // DBからマイルストーンを取得
  useEffect(() => {
    const projectIds = filteredProjects.map(p => p.id)
    if (projectIds.length === 0) {
      setLocalMilestones([])
      return
    }

    const fetchMilestones = async () => {
      setMilestonesLoading(true)
      try {
        const dbMilestones = await getMilestonesByProjectIds(projectIds)
        setLocalMilestones(dbMilestones.map(convertDBMilestoneToLocal))
      } catch (error) {
        console.error('Failed to fetch milestones:', error)
      } finally {
        setMilestonesLoading(false)
      }
    }

    fetchMilestones()

    // リアルタイム同期: project_milestonesテーブルの変更を監視
    // チャンネル名はユニークにする（projectIdsのハッシュを含める）
    const channelName = `project_milestones_${projectIds.sort().join('_').slice(0, 50)}_${Date.now()}`
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_milestones'
        },
        (payload) => {
          // 関連するプロジェクトのマイルストーン変更のみ処理
          const changedProjectId = (payload.new as { project_id?: string })?.project_id ||
                                   (payload.old as { project_id?: string })?.project_id
          console.log('[GanttChartV2] Realtime payload received:', payload.eventType, changedProjectId)
          if (changedProjectId && projectIds.includes(changedProjectId)) {
            console.log('[GanttChartV2] Milestone change detected, refetching:', payload.eventType)
            fetchMilestones()
          }
        }
      )
      .subscribe((status) => {
        console.log('[GanttChartV2] Milestone channel status:', status)
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [filteredProjects])

  // DBからテンプレートを取得（チームIDまたはユーザーIDに基づく）
  useEffect(() => {
    // チームプロジェクトがある場合、最初のチームのテンプレートを取得
    const teamProject = filteredProjects.find(p => p.team_id)
    const teamId = teamProject?.team_id || selectedTeamId

    const fetchTemplates = async () => {
      setTemplatesLoading(true)
      try {
        if (teamId) {
          // チームテンプレートを取得
          const dbTemplates = await getMilestoneTemplatesByTeamId(teamId)
          setLocalMilestoneTemplates(dbTemplates.map(convertDBTemplateToLocal))
        } else if (user?.id) {
          // 個人テンプレートを取得（初回は初期化）
          await initializePersonalTemplatesIfNeeded(user.id)
          const dbTemplates = await getMilestoneTemplatesByUserId(user.id)
          setLocalMilestoneTemplates(dbTemplates.map(convertDBTemplateToLocal))
        } else {
          setLocalMilestoneTemplates([])
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error)
      } finally {
        setTemplatesLoading(false)
      }
    }

    fetchTemplates()

    // リアルタイム同期: milestone_templatesテーブルの変更を監視
    const channelId = teamId || user?.id
    if (!channelId) return

    const channelName = `milestone_templates_${channelId}_${Date.now()}`
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'milestone_templates'
        },
        (payload) => {
          // 関連するチーム/ユーザーのテンプレート変更のみ処理
          const changedTeamId = (payload.new as { team_id?: string })?.team_id ||
                                (payload.old as { team_id?: string })?.team_id
          const changedUserId = (payload.new as { user_id?: string })?.user_id ||
                                (payload.old as { user_id?: string })?.user_id
          console.log('[GanttChartV2] Template realtime payload received:', payload.eventType, changedTeamId, changedUserId)
          if (changedTeamId === teamId || changedUserId === user?.id) {
            console.log('[GanttChartV2] Template change detected, refetching:', payload.eventType)
            fetchTemplates()
          }
        }
      )
      .subscribe((status) => {
        console.log('[GanttChartV2] Template channel status:', status)
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [filteredProjects, selectedTeamId, user?.id])

  // モーダル状態
  const [addTaskModal, setAddTaskModal] = useState<{ isOpen: boolean; projectId: string; projectName: string }>({
    isOpen: false,
    projectId: '',
    projectName: '',
  })
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  })

  // タスク詳細モーダル
  const [selectedTask, setSelectedTask] = useState<Todo | null>(null)

  // タスクバードラッグ/リサイズ用
  const [barDragState, setBarDragState] = useState<{
    taskId: string | null
    mode: 'move' | 'resize-left' | 'resize-right' | null
    startX: number
    originalStartDate: string
    originalEndDate: string
  }>({
    taskId: null,
    mode: null,
    startX: 0,
    originalStartDate: '',
    originalEndDate: '',
  })
  // ドラッグで実際に動いたかどうか（クリック判定用）
  const barDragMovedRef = useRef(false)

  // ドラッグ中のプレビュー日付（リアルタイム表示用）
  const [dragPreview, setDragPreview] = useState<{
    taskId: string
    startDate: string
    endDate: string
  } | null>(null)

  // バー移動/リサイズの確認ダイアログ用
  const [pendingDateChange, setPendingDateChange] = useState<{
    taskId: string
    taskTitle: string
    mode: 'move' | 'resize-left' | 'resize-right'
    oldStartDate: string
    oldEndDate: string
    newStartDate: string
    newEndDate: string
  } | null>(null)

  // マイルストーンテンプレート管理モーダル（ツールバーから開く）
  const [templateManagerModal, setTemplateManagerModal] = useState(false)

  // マイルストーン選択モーダル（日付クリック時 or 既存マイルストーンクリック時）
  const [milestoneSelectorModal, setMilestoneSelectorModal] = useState<{
    isOpen: boolean
    projectId: string
    projectName: string
    projectColor: string
    selectedDate: string
    existingMilestone: Milestone | null  // 既存マイルストーンをクリックした場合
  }>({
    isOpen: false,
    projectId: '',
    projectName: '',
    projectColor: '',
    selectedDate: '',
    existingMilestone: null,
  })

  // マイルストーンドラッグ用
  const [milestoneDragState, setMilestoneDragState] = useState<{
    milestoneId: string | null
    startX: number
    originalStartDate: string
  }>({
    milestoneId: null,
    startX: 0,
    originalStartDate: '',
  })
  const milestoneDragMovedRef = useRef(false)

  // マイルストーン日付変更確認用
  const [pendingMilestoneDateChange, setPendingMilestoneDateChange] = useState<{
    milestoneId: string
    milestoneTitle: string
    oldDate: string
    newDate: string
  } | null>(null)

  // ドラッグスクロール用（refベースで再レンダリング防止 - 本番と同等）
  const isScrollDraggingRef = useRef(false)
  const scrollStartXRef = useRef(0)
  const scrollStartYRef = useRef(0)
  const scrollStartLeftRef = useRef(0)
  const scrollStartTopRef = useRef(0)
  const lastXRef = useRef(0)
  const lastYRef = useRef(0)
  const lastTimeRef = useRef(0)
  const velocityXRef = useRef(0)
  const velocityYRef = useRef(0)
  const momentumAnimationRef = useRef<number | null>(null)

  // @dnd-kit用センサー設定（PC/モバイル別）
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px動かしてからドラッグ開始（誤操作防止）
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200, // 200ms長押しでドラッグ開始（モバイル誤操作防止）
        tolerance: 5, // 5px以内の動きは許容
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // スクロール参照
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const sidebarContentRef = useRef<HTMLDivElement>(null)  // サイドバー内部コンテンツ用
  const containerRef = useRef<HTMLDivElement>(null)

  // 日付ユーティリティ
  const formatDateFull = useCallback((date: Date): string => {
    return `${date.getMonth() + 1}/${date.getDate()}`
  }, [])

  const formatDateDay = useCallback((date: Date): string => {
    return `${date.getDate()}`
  }, [])

  const getDaysBetween = useCallback((start: Date, end: Date): number => {
    // 時刻を正規化して日数を正確に計算（タイムゾーン・時刻の影響を排除）
    const startNormalized = new Date(start.getFullYear(), start.getMonth(), start.getDate())
    const endNormalized = new Date(end.getFullYear(), end.getMonth(), end.getDate())
    return Math.round((endNormalized.getTime() - startNormalized.getTime()) / (1000 * 60 * 60 * 24))
  }, [])

  const addDays = useCallback((date: Date, days: number): Date => {
    const result = new Date(date)
    result.setDate(result.getDate() + days)
    return result
  }, [])

  // ローカルタイムゾーンでYYYY-MM-DD形式の日付文字列を取得
  // toISOString()はUTC基準のため、日本時間では日付が1日ずれる問題を回避
  const toLocalDateString = useCallback((date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }, [])

  // 週末かどうか
  const isWeekend = useCallback((date: Date): boolean => {
    const day = date.getDay()
    return day === 0 || day === 6
  }, [])

  // 今日かどうか
  const isToday = useCallback((date: Date): boolean => {
    return date.toDateString() === today.toDateString()
  }, [today])

  // 日付配列を生成
  const dates = useMemo(() => {
    const result: Date[] = []
    for (let i = 0; i < displayDays; i++) {
      result.push(addDays(startDate, i))
    }
    return result
  }, [startDate, displayDays, addDays])

  // 担当者フィルタ適用（ganttTasksに対して）
  const filteredTasks = useMemo(() => {
    let tasks = ganttTasks

    if (selectedAssigneeId) {
      tasks = tasks.filter(t => t.assigneeId === selectedAssigneeId)
    }

    return tasks
  }, [ganttTasks, selectedAssigneeId])

  // プロジェクトごとのマイルストーンを取得（ローカルステートから）
  const getMilestonesForProject = useCallback((projectId: string): Milestone[] => {
    return localMilestones
      .filter(m => m.projectId === projectId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
  }, [localMilestones])

  // プロジェクトごとにグループ化（ganttSortOrderでソート）
  const projectGroups = useMemo(() => {
    if (sortBy === 'custom') {
      return filteredProjects.map(project => ({
        project,
        milestones: getMilestonesForProject(project.id),
        tasks: filteredTasks
          .filter(t => t.projectId === project.id)
          .sort((a, b) => a.ganttSortOrder - b.ganttSortOrder)
      }))
    } else {
      // 担当者順：担当者名でソート後、ganttSortOrderでソート
      return filteredProjects.map(project => ({
        project,
        milestones: getMilestonesForProject(project.id),
        tasks: filteredTasks
          .filter(t => t.projectId === project.id)
          .sort((a, b) => {
            const aName = a.assigneeName || 'zzz'
            const bName = b.assigneeName || 'zzz'
            const nameCompare = aName.localeCompare(bName)
            if (nameCompare !== 0) return nameCompare
            return a.ganttSortOrder - b.ganttSortOrder
          })
      }))
    }
  }, [filteredProjects, filteredTasks, sortBy, getMilestonesForProject])

  // 日付からX座標を計算
  const getXPosition = useCallback((date: string): number => {
    // タイムゾーン問題を回避：日付文字列を直接パース
    const parts = date.split('-')
    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]))
    const daysDiff = getDaysBetween(startDate, d)
    return daysDiff * currentZoom.dayWidth
  }, [startDate, getDaysBetween, currentZoom.dayWidth])

  // タスクバーの幅を計算
  const getBarWidth = useCallback((start: string, end: string): number => {
    // タイムゾーン問題を回避：日付文字列を直接パース
    const startParts = start.split('-')
    const endParts = end.split('-')
    const startD = new Date(parseInt(startParts[0]), parseInt(startParts[1]) - 1, parseInt(startParts[2]))
    const endD = new Date(parseInt(endParts[0]), parseInt(endParts[1]) - 1, parseInt(endParts[2]))
    const days = getDaysBetween(startD, endD) + 1
    return days * currentZoom.dayWidth - 4
  }, [getDaysBetween, currentZoom.dayWidth])

  // スクロールを特定の日付に移動（左端に表示）
  const scrollToDate = useCallback((targetDate: Date) => {
    if (scrollContainerRef.current) {
      const targetX = getDaysBetween(startDate, targetDate) * currentZoom.dayWidth
      // 左端に表示（少し余白を持たせる）
      scrollContainerRef.current.scrollTo({
        left: Math.max(0, targetX - 20),
        behavior: 'smooth'
      })
    }
  }, [startDate, getDaysBetween, currentZoom.dayWidth])

  // タスクの開始日にスクロール（サイドバーのタスク名クリック時）
  const scrollToTask = useCallback((task: GanttTask) => {
    if (task.startDate) {
      const taskStartDate = new Date(task.startDate)
      scrollToDate(taskStartDate)
    } else if (task.endDate) {
      // 開始日がなければ終了日にスクロール
      const taskEndDate = new Date(task.endDate)
      scrollToDate(taskEndDate)
    }
  }, [scrollToDate])

  // ナビゲーション - 1週間前へ
  const goToPrevWeek = useCallback(() => {
    if (scrollContainerRef.current) {
      const currentScroll = scrollContainerRef.current.scrollLeft
      const weekPixels = 7 * currentZoom.dayWidth
      scrollContainerRef.current.scrollTo({
        left: currentScroll - weekPixels,
        behavior: 'smooth'
      })
    }
  }, [currentZoom.dayWidth])

  // ナビゲーション - 1週間後へ
  const goToNextWeek = useCallback(() => {
    if (scrollContainerRef.current) {
      const currentScroll = scrollContainerRef.current.scrollLeft
      const weekPixels = 7 * currentZoom.dayWidth
      scrollContainerRef.current.scrollTo({
        left: currentScroll + weekPixels,
        behavior: 'smooth'
      })
    }
  }, [currentZoom.dayWidth])

  // 今日へ移動
  const goToToday = useCallback(() => {
    scrollToDate(today)
  }, [scrollToDate, today])

  // ズーム
  const zoomIn = () => {
    if (zoomLevel > 0) setZoomLevel(zoomLevel - 1)
  }

  const zoomOut = () => {
    if (zoomLevel < ZOOM_LEVELS.length - 1) setZoomLevel(zoomLevel + 1)
  }

  const zoomReset = () => {
    setZoomLevel(DEFAULT_ZOOM_LEVEL)
  }

  // サイドバー切り替え
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  // 慣性スクロールアニメーション（本番と同等の実装）
  const applyMomentum = useCallback(() => {
    if (!scrollContainerRef.current) return

    const friction = 0.92 // 本番と同じ摩擦係数
    const minVelocity = 0.3 // 本番と同じ停止閾値

    const animate = () => {
      if (Math.abs(velocityXRef.current) < minVelocity && Math.abs(velocityYRef.current) < minVelocity) {
        momentumAnimationRef.current = null
        return
      }

      // 速度を適用
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollLeft += velocityXRef.current
        scrollContainerRef.current.scrollTop += velocityYRef.current
      }

      // 摩擦で減速
      velocityXRef.current *= friction
      velocityYRef.current *= friction

      momentumAnimationRef.current = requestAnimationFrame(animate)
    }

    momentumAnimationRef.current = requestAnimationFrame(animate)
  }, [])

  // 慣性スクロールをキャンセル
  const cancelMomentum = useCallback(() => {
    if (momentumAnimationRef.current) {
      cancelAnimationFrame(momentumAnimationRef.current)
      momentumAnimationRef.current = null
    }
    velocityXRef.current = 0
    velocityYRef.current = 0
  }, [])

  // ドラッグスクロール開始（refベースで再レンダリング防止）
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return // 左クリックのみ
    if (!scrollContainerRef.current) return

    // タスクバーやボタンでは開始しない
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return

    // 慣性スクロール中なら停止
    cancelMomentum()

    isScrollDraggingRef.current = true
    scrollStartXRef.current = e.clientX
    scrollStartYRef.current = e.clientY
    scrollStartLeftRef.current = scrollContainerRef.current.scrollLeft
    scrollStartTopRef.current = scrollContainerRef.current.scrollTop
    lastXRef.current = e.clientX
    lastYRef.current = e.clientY
    lastTimeRef.current = performance.now()
    velocityXRef.current = 0
    velocityYRef.current = 0

    // カーソルを変更
    document.body.style.cursor = 'grabbing'
  }, [cancelMomentum])

  // グローバルマウスイベント（本番と同等 - ドラッグ処理をここに集約）
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isScrollDraggingRef.current || !scrollContainerRef.current) return

      const clientX = e.clientX
      const clientY = e.clientY

      const dx = scrollStartXRef.current - clientX
      const dy = scrollStartYRef.current - clientY
      scrollContainerRef.current.scrollLeft = scrollStartLeftRef.current + dx
      scrollContainerRef.current.scrollTop = scrollStartTopRef.current + dy

      const currentTime = performance.now()
      const timeDelta = currentTime - lastTimeRef.current

      if (timeDelta > 0) {
        const xDelta = lastXRef.current - clientX
        const yDelta = lastYRef.current - clientY
        velocityXRef.current = (xDelta / timeDelta) * 16
        velocityYRef.current = (yDelta / timeDelta) * 16
      }

      lastXRef.current = clientX
      lastYRef.current = clientY
      lastTimeRef.current = currentTime
    }

    const handleGlobalMouseUp = () => {
      if (isScrollDraggingRef.current) {
        isScrollDraggingRef.current = false
        document.body.style.cursor = ''

        if (Math.abs(velocityXRef.current) > 1 || Math.abs(velocityYRef.current) > 1) {
          applyMomentum()
        }
      }
    }

    window.addEventListener('mousemove', handleGlobalMouseMove)
    window.addEventListener('mouseup', handleGlobalMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove)
      window.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [applyMomentum])

  // ヘッダーとボディのスクロール同期（水平・垂直）
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    const header = headerRef.current
    const sidebarContent = sidebarContentRef.current

    if (!scrollContainer || !header) return

    const handleScroll = () => {
      header.scrollLeft = scrollContainer.scrollLeft
      // サイドバーは overflow-hidden なので transform で移動させる
      if (sidebarContent) {
        sidebarContent.style.transform = `translateY(-${scrollContainer.scrollTop}px)`
      }
    }

    scrollContainer.addEventListener('scroll', handleScroll)
    return () => scrollContainer.removeEventListener('scroll', handleScroll)
  }, [])

  // 初期表示時に今日を中央に
  useEffect(() => {
    const timer = setTimeout(() => {
      goToToday()
    }, 100)
    return () => clearTimeout(timer)
  }, [goToToday])

  // プロジェクトカラーを取得
  const getProjectColor = useCallback((projectId: string): string => {
    const project = filteredProjects.find(p => p.id === projectId)
    return project?.color || '#6366f1'
  }, [filteredProjects])

  // タスク追加モーダルを開く
  const openAddTaskModal = useCallback((projectId: string) => {
    const project = filteredProjects.find(p => p.id === projectId)
    if (!project) return

    setAddTaskModal({
      isOpen: true,
      projectId,
      projectName: project.name,
    })
  }, [filteredProjects])

  // @dnd-kit ドラッグ終了ハンドラ（行の並び替え）
  const handleDragEnd = useCallback((event: DragEndEvent, projectId: string) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    // 該当プロジェクトのタスクを取得
    const projectTasks = filteredTasks
      .filter(t => t.projectId === projectId)
      .sort((a, b) => a.ganttSortOrder - b.ganttSortOrder)

    const oldIndex = projectTasks.findIndex(t => t.id === active.id)
    const newIndex = projectTasks.findIndex(t => t.id === over.id)

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return

    // ganttSortOrderの重複チェック
    const orderSet = new Set<number>()
    let hasDuplicates = false
    for (const task of projectTasks) {
      if (orderSet.has(task.ganttSortOrder)) {
        hasDuplicates = true
        break
      }
      orderSet.add(task.ganttSortOrder)
    }

    if (hasDuplicates) {
      // 重複がある場合: 全タスクの順序を再割り当て
      const reordered = [...projectTasks]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)

      const todoIds = reordered.map(t => t.id)
      const newOrders = reordered.map((_, index) => (index + 1) * 1000)
      updateGanttTodosOrder(todoIds, newOrders)
    } else {
      // 重複がない場合: Fractional Indexing
      let newOrder: number

      if (oldIndex < newIndex) {
        // 下に移動: over要素とその次の要素の間に配置
        const targetOrder = projectTasks[newIndex]?.ganttSortOrder ?? 0
        const nextOrder = projectTasks[newIndex + 1]?.ganttSortOrder ?? targetOrder + 2000
        newOrder = Math.floor((targetOrder + nextOrder) / 2)
      } else {
        // 上に移動: over要素の前の要素とover要素の間に配置
        const targetOrder = projectTasks[newIndex]?.ganttSortOrder ?? 0
        const prevOrder = projectTasks[newIndex - 1]?.ganttSortOrder ?? targetOrder - 2000
        newOrder = Math.floor((prevOrder + targetOrder) / 2)
      }

      // 単一タスクの更新（バッチ関数を1要素で呼び出し）
      updateGanttTodosOrder([active.id as string], [newOrder])
    }
  }, [filteredTasks, updateGanttTodosOrder])

  // マイルストーン選択モーダルを開く（日付クリック時 or 既存マイルストーンクリック時）
  const openMilestoneSelector = useCallback((projectId: string, date: string, existingMilestone?: Milestone) => {
    const project = filteredProjects.find(p => p.id === projectId)
    if (!project) return

    setMilestoneSelectorModal({
      isOpen: true,
      projectId,
      projectName: project.name,
      projectColor: project.color,
      selectedDate: date,
      existingMilestone: existingMilestone || null,
    })
  }, [filteredProjects])

  // テンプレートを選択してマイルストーンを配置（既存マイルストーンの場合は更新）
  const handlePlaceMilestone = useCallback(async (template: MilestoneTemplate) => {
    try {
      if (milestoneSelectorModal.existingMilestone) {
        // 既存マイルストーンの更新
        const updated = await updateProjectMilestone(milestoneSelectorModal.existingMilestone.id, {
          template_id: template.id,
          title: template.title,
          icon_type: template.iconType,
          icon_value: template.iconValue,
          color: template.color,
        })
        setLocalMilestones(prev =>
          prev.map(m => m.id === updated.id ? convertDBMilestoneToLocal(updated) : m)
        )
      } else {
        // 新規マイルストーンの作成
        const created = await createProjectMilestone({
          project_id: milestoneSelectorModal.projectId,
          template_id: template.id,
          title: template.title,
          start_date: milestoneSelectorModal.selectedDate,
          end_date: null,
          icon_type: template.iconType,
          icon_value: template.iconValue,
          color: template.color,
          sort_order: localMilestones.filter(m => m.projectId === milestoneSelectorModal.projectId).length + 1,
        })
        setLocalMilestones(prev => [...prev, convertDBMilestoneToLocal(created)])
      }
    } catch (error) {
      console.error('Failed to place milestone:', error)
    }
    setMilestoneSelectorModal({
      isOpen: false,
      projectId: '',
      projectName: '',
      projectColor: '',
      selectedDate: '',
      existingMilestone: null,
    })
  }, [milestoneSelectorModal, localMilestones])

  // マイルストーン削除
  const handleDeleteMilestone = useCallback(async (milestoneId: string) => {
    try {
      await deleteProjectMilestone(milestoneId)
      setLocalMilestones(prev => prev.filter(m => m.id !== milestoneId))
    } catch (error) {
      console.error('Failed to delete milestone:', error)
    }
  }, [])

  // マイルストーン日付変更（モーダルから）
  const handleMilestoneDateChange = useCallback(async (milestoneId: string, newDate: string) => {
    try {
      const updated = await updateProjectMilestone(milestoneId, {
        start_date: newDate,
      })
      setLocalMilestones(prev =>
        prev.map(m => m.id === updated.id ? convertDBMilestoneToLocal(updated) : m)
      )
    } catch (error) {
      console.error('Failed to update milestone date:', error)
    }
  }, [])

  // 現在のチームIDを取得するヘルパー
  const getCurrentTeamId = useCallback(() => {
    const teamProject = filteredProjects.find(p => p.team_id)
    return teamProject?.team_id || selectedTeamId || null
  }, [filteredProjects, selectedTeamId])

  // テンプレート追加
  const handleAddTemplate = useCallback(async (template: Partial<MilestoneTemplate>) => {
    const teamId = getCurrentTeamId()
    try {
      let created: DBMilestoneTemplate
      if (teamId) {
        // チームテンプレートを作成
        created = await createMilestoneTemplate({
          team_id: teamId,
          title: template.title || '',
          icon_type: template.iconType || 'emoji',
          icon_value: template.iconValue || '📋',
          color: template.color || '#6366f1',
          sort_order: template.sortOrder || localMilestoneTemplates.length,
        })
      } else if (user?.id) {
        // 個人テンプレートを作成
        created = await createPersonalMilestoneTemplate(user.id, {
          title: template.title || '',
          icon_type: template.iconType || 'emoji',
          icon_value: template.iconValue || '📋',
          color: template.color || '#6366f1',
          sort_order: template.sortOrder || localMilestoneTemplates.length,
        })
      } else {
        console.error('No team ID or user ID available for creating template')
        return
      }
      setLocalMilestoneTemplates(prev => [...prev, convertDBTemplateToLocal(created)])
    } catch (error) {
      console.error('Failed to create template:', error)
    }
  }, [getCurrentTeamId, localMilestoneTemplates.length, user?.id])

  // テンプレート更新
  const handleUpdateTemplate = useCallback(async (templateId: string, updates: Partial<MilestoneTemplate>) => {
    try {
      const updated = await updateMilestoneTemplate(templateId, {
        title: updates.title,
        icon_type: updates.iconType,
        icon_value: updates.iconValue,
        color: updates.color,
        sort_order: updates.sortOrder,
      })
      setLocalMilestoneTemplates(prev =>
        prev.map(t => t.id === updated.id ? convertDBTemplateToLocal(updated) : t)
      )
    } catch (error) {
      console.error('Failed to update template:', error)
    }
  }, [])

  // テンプレート削除
  const handleDeleteTemplate = useCallback(async (templateId: string) => {
    try {
      await deleteMilestoneTemplate(templateId)
      setLocalMilestoneTemplates(prev => prev.filter(t => t.id !== templateId))
    } catch (error) {
      console.error('Failed to delete template:', error)
    }
  }, [])



  // マイルストーンドラッグ開始（マウス・タッチ対応）
  const handleMilestoneDragStart = useCallback((
    e: React.MouseEvent | React.TouchEvent,
    milestone: Milestone
  ) => {
    e.stopPropagation()
    // タッチイベントの場合はpreventDefaultしない（スクロールを許可）
    if ('clientX' in e) {
      e.preventDefault()
    }

    const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX

    milestoneDragMovedRef.current = false

    setMilestoneDragState({
      milestoneId: milestone.id,
      startX: clientX,
      originalStartDate: milestone.startDate,
    })

    document.body.style.cursor = 'grabbing'
  }, [])

  // タスクバードラッグ開始（マウス・タッチ対応）
  const handleBarDragStart = useCallback((
    e: React.MouseEvent | React.TouchEvent,
    task: GanttTask,
    mode: 'move' | 'resize-left' | 'resize-right'
  ) => {
    e.stopPropagation()
    // マウス・タッチ両方でpreventDefaultを呼ぶ（iOSでドラッグを有効にするため）
    e.preventDefault()

    const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX

    barDragMovedRef.current = false // リセット

    setBarDragState({
      taskId: task.id,
      mode,
      startX: clientX,
      originalStartDate: task.startDate || '',
      originalEndDate: task.endDate || '',
    })

    // カーソル変更
    document.body.style.cursor = mode === 'move' ? 'grabbing' : 'ew-resize'
  }, [])

  // タスクバークリック（モーダルを開く）
  const handleTaskClick = useCallback((taskId: string) => {
    const todo = todos.find(t => t.id === taskId)
    if (todo) {
      setSelectedTask(todo)
    }
  }, [todos])

  // サイドバーの編集ボタンクリック（GanttTask経由）
  const handleEditButtonClick = useCallback((task: GanttTask) => {
    handleTaskClick(task.id)
  }, [handleTaskClick])

  // タスクバードラッグ中のグローバルイベント
  useEffect(() => {
    if (!barDragState.taskId) return

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - barDragState.startX
      const deltaDays = Math.round(deltaX / currentZoom.dayWidth)

      // 実際に動いたかどうか（5px以上で移動と判定）
      if (Math.abs(deltaX) > 5) {
        barDragMovedRef.current = true
      }

      // ドラッグ中のプレビュー日付を計算
      if (barDragMovedRef.current && barDragState.originalStartDate && barDragState.originalEndDate) {
        const originalStart = new Date(barDragState.originalStartDate)
        const originalEnd = new Date(barDragState.originalEndDate)

        let newStartDate: string
        let newEndDate: string

        if (barDragState.mode === 'move') {
          // 移動: 両方の日付を同じ日数だけシフト
          newStartDate = toLocalDateString(addDays(originalStart, deltaDays))
          newEndDate = toLocalDateString(addDays(originalEnd, deltaDays))
        } else if (barDragState.mode === 'resize-left') {
          // 左リサイズ: 開始日のみ変更（終了日は固定）
          const newStart = addDays(originalStart, deltaDays)
          // 開始日が終了日を超えないように（同日は許可 = 1日タスク）
          if (newStart <= originalEnd) {
            newStartDate = toLocalDateString(newStart)
          } else {
            newStartDate = toLocalDateString(originalEnd)
          }
          newEndDate = barDragState.originalEndDate
        } else {
          // 右リサイズ: 終了日のみ変更（開始日は固定）
          const newEnd = addDays(originalEnd, deltaDays)
          // 終了日が開始日より前にならないように（同日は許可 = 1日タスク）
          if (newEnd >= originalStart) {
            newEndDate = toLocalDateString(newEnd)
          } else {
            newEndDate = toLocalDateString(originalStart)
          }
          newStartDate = barDragState.originalStartDate
        }

        setDragPreview({
          taskId: barDragState.taskId!,
          startDate: newStartDate,
          endDate: newEndDate,
        })
      }
    }

    const handleMouseUp = () => {
      // ドラッグせずにマウスアップ = クリック → モーダルを開く
      if (!barDragMovedRef.current && barDragState.mode === 'move' && barDragState.taskId) {
        handleTaskClick(barDragState.taskId)
        setDragPreview(null)
      } else if (barDragMovedRef.current && dragPreview && barDragState.taskId) {
        // ドラッグで日付が変更された場合 → 確認ダイアログを表示（プレビューは維持）
        const task = ganttTasks.find(t => t.id === barDragState.taskId)
        if (task && (
          dragPreview.startDate !== barDragState.originalStartDate ||
          dragPreview.endDate !== barDragState.originalEndDate
        )) {
          setPendingDateChange({
            taskId: barDragState.taskId,
            taskTitle: task.title,
            mode: barDragState.mode!,
            oldStartDate: barDragState.originalStartDate,
            oldEndDate: barDragState.originalEndDate,
            newStartDate: dragPreview.startDate,
            newEndDate: dragPreview.endDate,
          })
          // プレビューは維持（確認ダイアログでOK/キャンセル時にリセット）
        } else {
          setDragPreview(null)
        }
      } else {
        setDragPreview(null)
      }

      setBarDragState({
        taskId: null,
        mode: null,
        startX: 0,
        originalStartDate: '',
        originalEndDate: '',
      })
      document.body.style.cursor = ''
    }

    // タッチイベント用ハンドラ
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0]
        handleMouseMove({ clientX: touch.clientX } as MouseEvent)
      }
    }

    const handleTouchEnd = () => {
      handleMouseUp()
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchmove', handleTouchMove, { passive: true })
    window.addEventListener('touchend', handleTouchEnd)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [barDragState, currentZoom.dayWidth, addDays, ganttTasks, handleTaskClick, dragPreview])

  // マイルストーンドラッグ中のグローバルイベント
  useEffect(() => {
    if (!milestoneDragState.milestoneId) return

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - milestoneDragState.startX
      const deltaDays = Math.round(deltaX / currentZoom.dayWidth)

      // 実際に動いたかどうか（5px以上で移動と判定）
      if (Math.abs(deltaX) > 5) {
        milestoneDragMovedRef.current = true
      }

      setLocalMilestones(prev =>
        prev.map(m => {
          if (m.id !== milestoneDragState.milestoneId) return m

          const originalStart = new Date(milestoneDragState.originalStartDate)
          const newStart = addDays(originalStart, deltaDays)

          // endDateがある場合は同じ日数だけシフト
          if (m.endDate) {
            const originalEnd = new Date(m.endDate)
            const daysDiff = getDaysBetween(originalStart, originalEnd)
            const newEnd = addDays(newStart, daysDiff)
            return {
              ...m,
              startDate: toLocalDateString(newStart),
              endDate: toLocalDateString(newEnd),
            }
          }

          return {
            ...m,
            startDate: toLocalDateString(newStart),
          }
        })
      )
    }

    const handleMouseUp = () => {
      const milestone = localMilestones.find(m => m.id === milestoneDragState.milestoneId)

      if (milestoneDragMovedRef.current) {
        // 実際に移動した場合 → 確認ダイアログを表示
        if (milestone) {
          const hasChanged = milestone.startDate !== milestoneDragState.originalStartDate

          if (hasChanged) {
            setPendingMilestoneDateChange({
              milestoneId: milestone.id,
              milestoneTitle: milestone.title,
              oldDate: milestoneDragState.originalStartDate,
              newDate: milestone.startDate,
            })
          }
        }
      } else {
        // クリック（移動なし）の場合 → 選択モーダルを開く（既存マイルストーンを渡す）
        if (milestone) {
          openMilestoneSelector(milestone.projectId, milestone.startDate, milestone)
        }
      }

      setMilestoneDragState({
        milestoneId: null,
        startX: 0,
        originalStartDate: '',
      })
      document.body.style.cursor = ''
    }

    // タッチイベント用ハンドラ
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        const touch = e.touches[0]
        handleMouseMove({ clientX: touch.clientX } as MouseEvent)
      }
    }

    const handleTouchEnd = () => {
      handleMouseUp()
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchmove', handleTouchMove, { passive: true })
    window.addEventListener('touchend', handleTouchEnd)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [milestoneDragState, currentZoom.dayWidth, addDays, getDaysBetween, localMilestones, openMilestoneSelector])

  // サイドバー幅
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_WIDTH_NARROW : SIDEBAR_WIDTH_WIDE

  return (
    <div
      ref={containerRef}
      className={`flex flex-col bg-white h-full min-h-0 ${className}`}
    >
      {/* モーダル */}
      <AddTaskModal
        isOpen={addTaskModal.isOpen}
        onClose={() => setAddTaskModal({ isOpen: false, projectId: '', projectName: '' })}
        defaultProjectId={addTaskModal.projectId || null}
      />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
      />
      {/* タスク詳細モーダル（共通コンポーネント） */}
      <TaskDetailModal
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
        getProject={(projectId) => projects.find(p => p.id === projectId)}
      />
      {/* マイルストーンテンプレート管理モーダル（新コンポーネント） */}
      <MilestoneTemplateModal
        isOpen={templateManagerModal}
        onClose={() => setTemplateManagerModal(false)}
        templates={localMilestoneTemplates}
        teamName="チーム"
        onAdd={handleAddTemplate}
        onUpdate={handleUpdateTemplate}
        onDelete={handleDeleteTemplate}
      />
      <MilestoneSelectorModal
        isOpen={milestoneSelectorModal.isOpen}
        onClose={() => setMilestoneSelectorModal({ isOpen: false, projectId: '', projectName: '', projectColor: '', selectedDate: '', existingMilestone: null })}
        projectId={milestoneSelectorModal.projectId}
        projectName={milestoneSelectorModal.projectName}
        projectColor={milestoneSelectorModal.projectColor}
        selectedDate={milestoneSelectorModal.selectedDate}
        templates={localMilestoneTemplates}
        existingMilestone={milestoneSelectorModal.existingMilestone}
        onSelect={handlePlaceMilestone}
        onDelete={handleDeleteMilestone}
        onDateChange={handleMilestoneDateChange}
        onOpenTemplateEditor={() => {
          setMilestoneSelectorModal({ isOpen: false, projectId: '', projectName: '', projectColor: '', selectedDate: '', existingMilestone: null })
          setTemplateManagerModal(true)
        }}
      />
      <MilestoneDateChangeDialog
        isOpen={!!pendingMilestoneDateChange}
        onClose={() => {
          // キャンセル時は日付を元に戻す
          if (pendingMilestoneDateChange) {
            setLocalMilestones(prev =>
              prev.map(m =>
                m.id === pendingMilestoneDateChange.milestoneId
                  ? { ...m, startDate: pendingMilestoneDateChange.oldDate }
                  : m
              )
            )
          }
          setPendingMilestoneDateChange(null)
        }}
        onConfirm={async () => {
          // 確定時にDBを更新
          if (pendingMilestoneDateChange) {
            try {
              const updated = await updateProjectMilestone(pendingMilestoneDateChange.milestoneId, {
                start_date: pendingMilestoneDateChange.newDate,
              })
              // 成功時: DBの結果でローカル状態を確定（再取得による上書きを防ぐ）
              setLocalMilestones(prev =>
                prev.map(m =>
                  m.id === updated.id ? convertDBMilestoneToLocal(updated) : m
                )
              )
            } catch (error) {
              console.error('Failed to update milestone date:', error)
              // エラー時は日付を元に戻す
              setLocalMilestones(prev =>
                prev.map(m =>
                  m.id === pendingMilestoneDateChange.milestoneId
                    ? { ...m, startDate: pendingMilestoneDateChange.oldDate }
                    : m
                )
              )
            }
          }
          setPendingMilestoneDateChange(null)
        }}
        milestoneTitle={pendingMilestoneDateChange?.milestoneTitle || ''}
        oldDate={pendingMilestoneDateChange?.oldDate || ''}
        newDate={pendingMilestoneDateChange?.newDate || ''}
      />

      {/* タスクバー日付変更確認ダイアログ */}
      {pendingDateChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {pendingDateChange.mode === 'move' ? 'タスクを移動' :
               pendingDateChange.mode === 'resize-left' ? '開始日を変更' : '終了日を変更'}
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              「{pendingDateChange.taskTitle}」
            </p>
            <div className="text-sm mb-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-gray-500">変更前:</span>
                <span>{pendingDateChange.oldStartDate} 〜 {pendingDateChange.oldEndDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">変更後:</span>
                <span className="font-medium text-indigo-600">
                  {pendingDateChange.newStartDate} 〜 {pendingDateChange.newEndDate}
                </span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setPendingDateChange(null)
                  setDragPreview(null)
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded"
              >
                キャンセル
              </button>
              <button
                onClick={async () => {
                  // 日付を更新
                  await updateTodo(pendingDateChange.taskId, {
                    start_date: pendingDateChange.newStartDate,
                    due_date: pendingDateChange.newEndDate,
                  })
                  setPendingDateChange(null)
                  setDragPreview(null)
                }}
                className="px-4 py-2 text-sm bg-indigo-600 text-white hover:bg-indigo-700 rounded"
              >
                変更を保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ツールバー - モバイルは2段組み、PCは1段 */}
      <div className="border-b bg-gray-50 flex-shrink-0">
        {/* PC: 1行表示 */}
        <div className="hidden md:flex items-center justify-between p-2">
          <div className="flex items-center gap-2">
            {/* サイドバー切り替え */}
            <button
              onClick={toggleSidebar}
              className="p-1.5 hover:bg-gray-200 rounded"
              title={sidebarCollapsed ? 'サイドバーを表示' : 'サイドバーを隠す'}
            >
              {sidebarCollapsed ? (
                <PanelLeft className="w-4 h-4" />
              ) : (
                <PanelLeftClose className="w-4 h-4" />
              )}
            </button>

            <div className="w-px h-6 bg-gray-300" />

            {/* チーム選択（プロジェクト選択時は無効） */}
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4 text-gray-500" />
              <select
                value={selectedTeamId || ''}
                onChange={(e) => setSelectedTeamId(e.target.value || null)}
                className="text-sm border rounded px-2 py-1 disabled:opacity-50"
                disabled={!!selectedProjectId}
                title={selectedProjectId ? 'プロジェクト選択中はチームフィルタ無効' : 'チームでフィルタ'}
              >
                <option value="">全て</option>
                {availableTeams.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>

            {/* 並び替え */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'custom' | 'assignee')}
              className="text-sm border rounded px-2 py-1"
            >
              <option value="custom">カスタム順</option>
              <option value="assignee">担当者順</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            {/* マイルストーン設定 */}
            <button
              onClick={() => setTemplateManagerModal(true)}
              className="p-1.5 hover:bg-amber-100 rounded flex items-center gap-1 text-amber-700 border border-amber-300 bg-amber-50"
              title="マイルストーン設定"
            >
              <Flag className="w-4 h-4" />
              <span className="text-xs">MS設定</span>
            </button>

            <div className="w-px h-6 bg-gray-300" />

            {/* ズーム */}
            <div className="flex items-center gap-0.5 bg-gray-100 rounded px-1">
              <button
                onClick={zoomOut}
                disabled={zoomLevel === ZOOM_LEVELS.length - 1}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                title="縮小"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <button
                onClick={zoomReset}
                className="p-1 hover:bg-gray-200 rounded"
                title="デフォルトに戻す"
              >
                <Square className="w-3 h-3" />
              </button>
              <button
                onClick={zoomIn}
                disabled={zoomLevel === 0}
                className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                title="拡大"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            <div className="w-px h-6 bg-gray-300" />

            {/* 日付ナビゲーション */}
            <button
              onClick={goToPrevWeek}
              className="p-1 hover:bg-gray-200 rounded"
              title="1週間前へ"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goToToday}
              className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200"
            >
              今日
            </button>
            <button
              onClick={goToNextWeek}
              className="p-1 hover:bg-gray-200 rounded"
              title="1週間後へ"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* 区切り線 + 最大化ボタン */}
            <div className="w-px h-6 bg-gray-300" />
            {ganttFullscreenMode === 'none' && (
              <button
                onClick={() => setGanttFullscreenMode('with-sidebar')}
                className="p-1.5 hover:bg-gray-200 rounded"
                title="プロジェクト欄を残して拡大"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            )}
            {ganttFullscreenMode === 'with-sidebar' && (
              <button
                onClick={() => setGanttFullscreenMode('full')}
                className="p-1.5 hover:bg-gray-200 rounded"
                title="完全全画面表示"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            )}
            {ganttFullscreenMode === 'full' && (
              <button
                onClick={() => setGanttFullscreenMode('none')}
                className="p-1.5 hover:bg-gray-200 rounded"
                title="通常表示に戻す"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* モバイル: 1段表示（シンプル化） */}
        <div className="md:hidden flex items-center justify-between px-2 py-1.5">
          {/* 左側: サイドバー切り替え + チーム + ソート */}
          <div className="flex items-center gap-1.5">
            {/* サイドバー切り替え */}
            <button
              onClick={toggleSidebar}
              className="p-1 hover:bg-gray-200 rounded"
              title={sidebarCollapsed ? 'サイドバーを表示' : 'サイドバーを隠す'}
            >
              {sidebarCollapsed ? (
                <PanelLeft className="w-4 h-4" />
              ) : (
                <PanelLeftClose className="w-4 h-4" />
              )}
            </button>

            {/* チーム選択 */}
            <select
              value={selectedTeamId || ''}
              onChange={(e) => setSelectedTeamId(e.target.value || null)}
              className="text-xs border rounded px-1 py-0.5 max-w-[60px] disabled:opacity-50"
              disabled={!!selectedProjectId}
            >
              <option value="">全て</option>
              {availableTeams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>

            {/* 並び替え */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'custom' | 'assignee')}
              className="text-xs border rounded px-1 py-0.5"
            >
              <option value="custom">カスタム</option>
              <option value="assignee">担当者</option>
            </select>
          </div>

          {/* 右側: ズーム + 今日 */}
          <div className="flex items-center gap-2">
            {/* ズーム（－ □ ＋） */}
            <div className="flex items-center bg-gray-100 rounded-lg">
              <button
                onClick={zoomOut}
                disabled={zoomLevel === ZOOM_LEVELS.length - 1}
                className="p-1.5 hover:bg-gray-200 rounded-l-lg disabled:opacity-30 active:bg-gray-300"
                title="縮小"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <button
                onClick={zoomReset}
                className="p-1.5 hover:bg-gray-200 active:bg-gray-300"
                title="デフォルトに戻す"
              >
                <Square className="w-3 h-3" />
              </button>
              <button
                onClick={zoomIn}
                disabled={zoomLevel === 0}
                className="p-1.5 hover:bg-gray-200 rounded-r-lg disabled:opacity-30 active:bg-gray-300"
                title="拡大"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* 今日ボタン */}
            <button
              onClick={goToToday}
              className="px-3 py-1.5 text-sm font-medium bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 active:bg-indigo-300"
            >
              今日
            </button>
          </div>
        </div>
      </div>

      {/* ガント本体 */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* 左サイドバー */}
        <div
          className="flex-shrink-0 border-r bg-gray-50 flex flex-col transition-all duration-200 min-h-0"
          style={{ width: sidebarWidth }}
        >
          {/* サイドバーヘッダー */}
          <div
            className="flex-shrink-0 border-b bg-gray-100 flex items-center justify-center text-xs text-gray-500 font-medium"
            style={{ height: 40 }}
          >
            {!sidebarCollapsed && 'タスク名'}
          </div>

          {/* サイドバー本体（スクロール無効化、ガント側のスクロールに完全連動） */}
          <div ref={sidebarRef} className="flex-1 overflow-hidden">
            {/* 内部コンテンツをtransformで移動 */}
            <div ref={sidebarContentRef} className="touch-none">
            {projectGroups.map(({ project, milestones, tasks }, groupIndex) => (
              <div key={project.id}>
                {/* プロジェクトヘッダー */}
                <div
                  className="flex items-center gap-1 px-2 font-medium text-sm border-b"
                  style={{
                    height: PROJECT_HEADER_HEIGHT,
                    backgroundColor: project.color + '20',
                    borderLeftWidth: 4,
                    borderLeftColor: project.color,
                  }}
                >
                  <FolderOpen className="w-4 h-4 flex-shrink-0" style={{ color: project.color }} />
                  {!sidebarCollapsed && (
                    <span className="truncate flex-1">{project.name}</span>
                  )}
                </div>

                {/* マイルストーン行ラベル + ToDoを追加ボタン */}
                <div
                  className={`flex items-center justify-between px-2 text-xs border-b ${
                    milestones.length > 0 ? 'text-gray-500 bg-amber-50' : 'text-gray-400 bg-amber-50/50'
                  }`}
                  style={{ height: MILESTONE_ROW_HEIGHT }}
                >
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                    {!sidebarCollapsed && (milestones.length > 0 ? 'マイルストーン' : 'MS行')}
                  </div>
                  {!sidebarCollapsed && (
                    <button
                      onClick={() => openAddTaskModal(project.id)}
                      className="flex items-center gap-1 px-2 py-0.5 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 transition-colors"
                      title="ToDoを追加"
                    >
                      <Plus className="w-3 h-3" />
                      <span>ToDoを追加</span>
                    </button>
                  )}
                  {sidebarCollapsed && (
                    <button
                      onClick={() => openAddTaskModal(project.id)}
                      className="p-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                      title="ToDoを追加"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* タスク行ラベル（@dnd-kitでスムーズドラッグ） */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => handleDragEnd(event, project.id)}
                >
                  <SortableContext
                    items={tasks.map(t => t.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {tasks.map(task => (
                      <SortableTaskRow
                        key={task.id}
                        task={task}
                        sidebarCollapsed={sidebarCollapsed}
                        sortBy={sortBy}
                        onClick={scrollToTask}
                        onEditClick={handleEditButtonClick}
                      />
                    ))}
                  </SortableContext>

                </DndContext>

                {/* プロジェクト間の区切り線 */}
                {groupIndex < projectGroups.length - 1 && (
                  <div className="h-2 bg-gray-200 border-b border-gray-300" />
                )}
              </div>
            ))}
            {/* 水平スクロールバーの高さ分のダミースペース（左右のスクロール最大値を揃える） */}
            <div className="h-[17px] flex-shrink-0" />
            </div>
          </div>
        </div>

        {/* 右側（ガントチャート） */}
        <div className="flex-1 flex flex-col overflow-hidden min-h-0">
          {/* 日付ヘッダー */}
          <div
            ref={headerRef}
            className="flex-shrink-0 overflow-hidden border-b"
            style={{ height: 40 }}
          >
            <div className="flex" style={{ width: dates.length * currentZoom.dayWidth }}>
              {dates.map((date, i) => {
                // ラベル表示
                const showFullDate = currentZoom.dateFormat === 'full'
                const showDayOnly = currentZoom.dateFormat === 'day'
                const isMonday = date.getDay() === 1
                const isFirstOfMonth = date.getDate() === 1

                // 週表示時は月曜または月初のみ
                const showLabel = showFullDate ||
                  (showDayOnly && (zoomLevel <= 2 || isMonday || isFirstOfMonth))

                return (
                  <div
                    key={i}
                    className={`flex-shrink-0 flex flex-col items-center justify-center text-xs border-r ${
                      isToday(date)
                        ? 'bg-indigo-100 text-indigo-700 font-bold'
                        : isWeekend(date)
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-white'
                    }`}
                    style={{ width: currentZoom.dayWidth }}
                  >
                    {showLabel && (
                      <>
                        <span className="truncate text-[10px]">
                          {showFullDate ? formatDateFull(date) : formatDateDay(date)}
                        </span>
                        {currentZoom.showDayOfWeek && (
                          <span className="text-[9px]">
                            {['日', '月', '火', '水', '木', '金', '土'][date.getDay()]}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* ガントチャート本体 */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-auto cursor-grab select-none"
            onMouseDown={handleMouseDown}
          >
            <div className="relative" style={{ width: dates.length * currentZoom.dayWidth }}>
              {/* 今日の赤線（ガント全体を貫通） */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 pointer-events-none z-30"
                style={{
                  left: getXPosition(toLocalDateString(today)) + currentZoom.dayWidth / 2,
                }}
              />
              {projectGroups.map(({ project, milestones, tasks }, groupIndex) => (
                <div key={project.id}>
                  {/* プロジェクトヘッダー行 */}
                  <div
                    className="relative border-b"
                    style={{
                      height: PROJECT_HEADER_HEIGHT,
                      backgroundColor: project.color + '20',
                    }}
                  >
                    {/* サイドバー折りたたみ時のフローティングプロジェクト名 */}
                    {sidebarCollapsed && (
                      <div
                        className="sticky left-0 z-40 flex items-center gap-1 px-2 h-full pointer-events-none"
                        style={{
                          backgroundColor: project.color + '90',
                          width: 'fit-content',
                          maxWidth: '50%',
                        }}
                      >
                        <FolderOpen className="w-3 h-3 flex-shrink-0 text-white" />
                        <span className="text-xs font-medium text-white truncate">{project.name}</span>
                      </div>
                    )}
                  </div>

                  {/* マイルストーン行（クリックでマイルストーン配置） */}
                  <div
                    className={`relative border-b ${milestones.length > 0 ? 'bg-amber-50' : 'bg-amber-50/30'}`}
                    style={{ height: MILESTONE_ROW_HEIGHT }}
                  >
                    {/* クリック可能な日付セル */}
                    <div className="absolute inset-0 flex">
                      {dates.map((date, i) => {
                        const dateStr = toLocalDateString(date)
                        const hasMilestone = milestones.some(m => m.startDate === dateStr)
                        return (
                          <div
                            key={i}
                            className={`flex-shrink-0 border-r cursor-pointer transition-colors ${
                              isWeekend(date)
                                ? milestones.length > 0 ? 'bg-amber-100/50 hover:bg-amber-200/70' : 'bg-amber-100/30 hover:bg-amber-200/50'
                                : 'hover:bg-amber-100/50'
                            } ${hasMilestone ? '' : 'hover:ring-1 hover:ring-inset hover:ring-amber-400'}`}
                            style={{ width: currentZoom.dayWidth }}
                            onClick={() => !hasMilestone && openMilestoneSelector(project.id, dateStr)}
                            title={hasMilestone ? '' : `${dateStr}\nクリックでマイルストーンを配置`}
                          />
                        )
                      })}
                    </div>

                    {/* マイルストーンアイコン（ドラッグ移動対応） */}
                    {milestones.map(milestone => {
                      const x = getXPosition(milestone.startDate)
                      const isInRange = x >= -currentZoom.dayWidth && x < dates.length * currentZoom.dayWidth
                      const isDragging = milestoneDragState.milestoneId === milestone.id

                      if (!isInRange) return null

                      return (
                        <div
                          key={milestone.id}
                          className={`absolute flex flex-col items-center z-20 cursor-grab active:cursor-grabbing pointer-events-auto transition-transform ${
                            isDragging ? 'scale-125 opacity-80' : 'hover:scale-110'
                          }`}
                          style={{
                            left: x,
                            top: 0,
                            width: currentZoom.dayWidth,
                            height: MILESTONE_ROW_HEIGHT,
                          }}
                          title={`${milestone.title}\n${milestone.startDate}\n（ドラッグで移動）`}
                          data-no-drag
                          onMouseDown={(e) => handleMilestoneDragStart(e, milestone)}
                          onTouchStart={(e) => handleMilestoneDragStart(e, milestone)}
                        >
                          <div
                            className="flex items-center justify-center w-full"
                            style={{ height: MILESTONE_ROW_HEIGHT }}
                          >
                            <span className={`text-xl leading-none ${isDragging ? 'drop-shadow-lg' : ''}`}>
                              {milestone.iconValue}
                            </span>
                          </div>
                          {zoomLevel <= 1 && (
                            <span
                              className="absolute -bottom-0.5 text-[9px] font-medium whitespace-nowrap px-1 rounded"
                              style={{ backgroundColor: milestone.color + '30', color: milestone.color }}
                            >
                              {milestone.title}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  {/* タスク行（ドラッグ中はアニメーションなし - 軽量化） */}
                  {tasks.map(task => {
                    // 開始日または終了日がないタスクはバー表示しない
                    if (!task.startDate || !task.endDate) {
                      return (
                        <div
                          key={task.id}
                          className="relative border-b bg-white"
                          style={{ height: ROW_HEIGHT }}
                        >
                          {/* サイドバー折りたたみ時のフローティングタスク名 */}
                          {sidebarCollapsed && (
                            <div
                              className="sticky left-0 z-40 flex items-center h-full pointer-events-none"
                              style={{ width: 'fit-content', maxWidth: '40%' }}
                            >
                              <span
                                className="text-[10px] text-gray-400 px-1 py-0.5 bg-white/90 border-r border-gray-200 truncate"
                                style={{ maxWidth: '100%' }}
                              >
                                {task.title}
                              </span>
                            </div>
                          )}
                          {/* グリッド線のみ表示 */}
                          <div className="absolute inset-0 flex pointer-events-none">
                            {dates.map((date, i) => (
                              <div
                                key={i}
                                className={`flex-shrink-0 border-r ${
                                  isWeekend(date) ? 'bg-gray-50' : ''
                                }`}
                                style={{ width: currentZoom.dayWidth }}
                              />
                            ))}
                          </div>
                        </div>
                      )
                    }

                    // ドラッグ中のプレビュー日付を使用
                    const displayStartDate = dragPreview?.taskId === task.id ? dragPreview.startDate : task.startDate
                    const displayEndDate = dragPreview?.taskId === task.id ? dragPreview.endDate : task.endDate
                    const x = getXPosition(displayStartDate)
                    const width = getBarWidth(displayStartDate, displayEndDate)
                    const projectColor = getProjectColor(task.projectId)
                    const isDragging = barDragState.taskId === task.id

                    // バー内に表示するテキストの幅を判定
                    const showProgressInBar = width > 40

                    return (
                      <div
                        key={task.id}
                        className="relative border-b bg-white"
                        style={{ height: ROW_HEIGHT }}
                      >
                        {/* サイドバー折りたたみ時のフローティングタスク名 */}
                        {sidebarCollapsed && (
                          <div
                            className="sticky left-0 z-40 flex items-center h-full pointer-events-none"
                            style={{ width: 'fit-content', maxWidth: '40%' }}
                          >
                            <span
                              className="text-[10px] text-gray-700 px-1 py-0.5 bg-white/90 border-r border-gray-200 truncate"
                              style={{ maxWidth: '100%' }}
                            >
                              {task.title}
                            </span>
                          </div>
                        )}

                        {/* グリッド線 */}
                        <div className="absolute inset-0 flex pointer-events-none">
                          {dates.map((date, i) => (
                            <div
                              key={i}
                              className={`flex-shrink-0 border-r ${
                                isWeekend(date) ? 'bg-gray-50' : ''
                              }`}
                              style={{ width: currentZoom.dayWidth }}
                            />
                          ))}
                        </div>

                        {/* タスクバー（ドラッグ移動・リサイズ対応） */}
                        <div
                          data-no-drag
                          className={`absolute top-1 rounded flex items-center overflow-visible z-20 pointer-events-auto group transition-all ${
                            isDragging ? 'ring-2 ring-indigo-500 shadow-lg opacity-90' : ''
                          }`}
                          style={{
                            left: x + 2,
                            width: Math.max(width, 20),
                            height: ROW_HEIGHT - 8,
                            backgroundColor: projectColor,
                            touchAction: 'none', // iOSでタッチドラッグを有効にするため
                          }}
                          title={`${task.title}\n担当: ${task.assigneeName || '未割り当て'}\n期間: ${displayStartDate} 〜 ${displayEndDate}\n進捗: ${task.progress}%`}
                        >
                          {/* 左リサイズハンドル */}
                          <div
                            className="absolute left-0 top-0 bottom-0 w-3 md:w-2 cursor-ew-resize hover:bg-white/30 z-30 flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                            style={{ touchAction: 'none' }}
                            onMouseDown={(e) => handleBarDragStart(e, task, 'resize-left')}
                            onTouchStart={(e) => handleBarDragStart(e, task, 'resize-left')}
                          >
                            <div className="w-0.5 h-3 bg-white/70 rounded" />
                          </div>

                          {/* バー中央（クリックでモーダルを開く、タッチでドラッグ） */}
                          <div
                            className="absolute left-3 right-3 md:left-2 md:right-2 top-0 bottom-0 cursor-pointer"
                            style={{ touchAction: 'none' }}
                            onMouseDown={(e) => handleBarDragStart(e, task, 'move')}
                            onTouchStart={(e) => handleBarDragStart(e, task, 'move')}
                          />

                          {/* 右リサイズハンドル */}
                          <div
                            className="absolute right-0 top-0 bottom-0 w-3 md:w-2 cursor-ew-resize hover:bg-white/30 z-30 flex items-center justify-center md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                            style={{ touchAction: 'none' }}
                            onMouseDown={(e) => handleBarDragStart(e, task, 'resize-right')}
                            onTouchStart={(e) => handleBarDragStart(e, task, 'resize-right')}
                          >
                            <div className="w-0.5 h-3 bg-white/70 rounded" />
                          </div>

                          {/* 進捗バー（背景） */}
                          <div
                            className="absolute inset-0 bg-black/20 pointer-events-none rounded-r"
                            style={{ left: `${task.progress}%` }}
                          />

                          {/* バー内コンテンツ */}
                          <div className="relative flex items-center gap-1 px-2 text-white w-full h-full pointer-events-none">
                            {/* 担当者バッジ */}
                            {task.assigneeId && task.assigneeName && (
                              <span
                                className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold flex-shrink-0 border border-white/50 bg-gradient-to-br ${task.avatarGradient}`}
                                title={task.assigneeName}
                              >
                                {task.assigneeInitial}
                              </span>
                            )}

                            {/* 進捗率 */}
                            {showProgressInBar && (
                              <span className="text-xs font-bold flex-shrink-0">
                                {task.progress}%
                              </span>
                            )}
                          </div>
                        </div>

                        {/* バー外に進捗表示（バーが小さい場合） */}
                        {!showProgressInBar && (
                          <span
                            className="absolute text-[10px] font-bold z-20 pointer-events-none"
                            style={{
                              left: x + width + 6,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: projectColor,
                            }}
                          >
                            {task.progress}%
                          </span>
                        )}
                      </div>
                    )
                  })}

                  {/* プロジェクト間の区切り */}
                  {groupIndex < projectGroups.length - 1 && (
                    <div className="h-2 bg-gray-200 border-b border-gray-300" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GanttChartV2
